import { supabase, isCloudEnabled } from './supabaseClient';
import { computeMetrics } from './bodyMetrics';
import { getPackCompletion, buildPackFollowupSchedule, isContinuousFollowupPack } from './services/packSystem';
import { classerEchec, fusionnerDansFile, resumerQuarantaine, DECISION_ECHEC } from './services/syncQueuePolicy';
import {
  deciderPousseeClient,
  lectureEstSuspecte,
  marquerVusEnLigne,
  dejaVuEnLigne,
} from './services/resurrectionGuard';
import {
  normalizeNutritionConsultationCloudRow,
  persistNutritionConsultationCloudConfirmed,
} from './services/nutritionConsultationPersistence';
import {
  matchesScheduledReview,
  publishRenewalOfferIfEligible,
} from './services/renewalOfferPublisher';

// 2026-08-08 — Retrait du compte Benoit du SaaS Anissa Nutrition.
//
// `created_by` retombait sur 'benoit' partout où la valeur manquait, héritage
// de l'époque où ce dépôt servait aussi le cockpit BenFitCoach. Conséquence :
// toute fiche créée sans attribution explicite était étiquetée au nom d'un
// praticien qui n'exerce plus ici, et les filtres historiques la rangeaient
// du mauvais côté. Le SaaS n'a plus qu'une praticienne : c'est elle le défaut.
//
// Ce n'est PAS un contrôle d'accès — celui-ci vit dans la policy RLS
// `clients_anissa_seule` (V97.178), qui compare `auth.uid()` au compte
// rattaché à `praticiens.code = 'anissa'`. Ici on ne fait qu'attribuer.
export const PRATICIENNE_PAR_DEFAUT = 'anissa';

async function getCurrentOwnerId() {
  if (!supabase) return null;
  const { data: { user } } = await supabase.auth.getUser();
  return user?.id || null;
}

function toNumOrNull(v) {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(String(v).replace(',', '.'));
  return Number.isFinite(n) ? n : null;
}

function round1OrNull(v) {
  if (v == null || !Number.isFinite(v)) return null;
  return Math.round(v * 10) / 10;
}

const STORAGE_KEY = 'bfc_clients';
const NUTRITION_KEY = 'bfc_nutrition_consultations';
const SYNC_QUEUE_KEY = 'bfc_sync_queue';
const NOTIFICATIONS_KEY = 'bfc_notifications';
const DELETED_IDS_KEY = 'bfc_deleted_ids';

// ─── localStorage helpers (always synchronous) ───

function readAll() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
  } catch {
    return [];
  }
}

function writeAll(clients) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(clients));
}

function readNutritionConsultations() {
  try {
    const all = JSON.parse(localStorage.getItem(NUTRITION_KEY) || '[]');
    // V78 : filtrer les consultations soft-delete a la lecture.
    // Les lignes restent en localStorage + DB (reversibles), mais invisibles pour l'app.
    return all.filter(c => !c.isDeleted);
  } catch {
    return [];
  }
}

// V78 : lecture interne SANS filtre, utilisee par softDeleteConsultation
// pour retrouver une consultation deja soft-deletee et/ou pour re-sync.
function readNutritionConsultationsRaw() {
  try {
    return JSON.parse(localStorage.getItem(NUTRITION_KEY) || '[]');
  } catch {
    return [];
  }
}

function writeNutritionConsultations(consultations) {
  localStorage.setItem(NUTRITION_KEY, JSON.stringify(consultations));
}

// ─── Tombstone registry: tracks intentionally deleted client IDs ───
// Prevents pullFromCloud() from resurrecting deleted clients ("zombie" bug).

function readDeletedIds() {
  try {
    const raw = JSON.parse(localStorage.getItem(DELETED_IDS_KEY) || '[]');
    const cutoff = Date.now() - 90 * 24 * 60 * 60 * 1000;
    const result = new Map();
    for (const entry of raw) {
      // Compatibilité avec l'ancien format (simple string)
      if (typeof entry === 'string') {
        result.set(entry, Date.now());
      } else if (entry?.id && entry.deletedAt > cutoff) {
        result.set(entry.id, entry.deletedAt);
      }
    }
    // Réécrire avec les entrées purgées
    const toStore = [...result.entries()].map(([id, deletedAt]) => ({ id, deletedAt }));
    localStorage.setItem(DELETED_IDS_KEY, JSON.stringify(toStore));
    return result;
  } catch {
    return new Map();
  }
}

function addDeletedId(id) {
  const entries = readDeletedIds();
  entries.set(id, Date.now());
  const toStore = [...entries.entries()].map(([id, deletedAt]) => ({ id, deletedAt }));
  localStorage.setItem(DELETED_IDS_KEY, JSON.stringify(toStore));
}

// ─── Sync queue for offline operations ───

function getSyncQueue() {
  try {
    return JSON.parse(localStorage.getItem(SYNC_QUEUE_KEY) || '[]');
  } catch {
    return [];
  }
}

function saveSyncQueue(queue) {
  localStorage.setItem(SYNC_QUEUE_KEY, JSON.stringify(queue));
}

function addToSyncQueue(operation) {
  // V97.155 — une operation DEJA en quarantaine ne revient pas en file.
  //
  // Sans cette garde, le correctif ne tenait pas : `pullFromCloud` repousse a
  // chaque connexion toute consultation locale absente du cloud (store.js,
  // « Push local-only nutrition consultations »). Les 30 orphelines auraient
  // ete remises en file au demarrage suivant, et la boucle se serait
  // reamorcee malgre la quarantaine.
  if (estEnQuarantaine(operation)) return;
  // V97.153 — fusion au lieu d'empilement. Avant, chaque sauvegarde d'une
  // meme ligne ajoutait une copie : la file grossissait sans fin.
  saveSyncQueue(fusionnerDansFile(getSyncQueue(), operation, Date.now()));
}

function pendingClientUpsertFingerprints(clientId) {
  if (!clientId) return new Set();
  return new Set(getSyncQueue()
    .filter((operation) => operation?.type === 'upsert_client' && operation?.data?.id === clientId)
    .map((operation) => JSON.stringify(operation.data)));
}

function clearPendingClientUpserts(clientId, staleFingerprints) {
  if (!clientId || !(staleFingerprints instanceof Set) || staleFingerprints.size === 0) return;
  saveSyncQueue(getSyncQueue().filter((operation) => !(
    operation?.type === 'upsert_client'
    && operation?.data?.id === clientId
    && staleFingerprints.has(JSON.stringify(operation.data))
  )));
}

// ─── Quarantaine (2026-08-01) ─────────────────────────────────────────
//
// Une operation qu'aucun rejeu ne peut faire aboutir sort de la file et
// vient ici. Elle n'est PAS supprimee : la donnee reste consultable,
// exportable et rejouable si la cause est corrigee. C'est la consigne
// d'Anissa — jamais de suppression silencieuse.
const QUARANTINE_KEY = 'bfc_sync_quarantine';

function getQuarantine() {
  try {
    return JSON.parse(localStorage.getItem(QUARANTINE_KEY) || '[]');
  } catch {
    return [];
  }
}

function saveQuarantine(liste) {
  try { localStorage.setItem(QUARANTINE_KEY, JSON.stringify(liste)); } catch { /* quota */ }
}

// ─── Registre local des suppressions définitives (miroir serveur) ─────
//
// V97.157 — sert à savoir si une clé étrangère manquante est DÉFINITIVE
// (cliente purgée volontairement) ou seulement TEMPORAIRE (cliente pas
// encore synchronisée). Sans cette distinction, on condamnerait une
// consultation légitime arrivée avant sa cliente.
const TOMBSTONES_KEY = 'bfc_tombstones_clients';

function getTombstones() {
  try {
    return new Set(JSON.parse(localStorage.getItem(TOMBSTONES_KEY) || '[]'));
  } catch {
    return new Set();
  }
}

function saveTombstones(ids) {
  try { localStorage.setItem(TOMBSTONES_KEY, JSON.stringify([...ids])); } catch { /* quota */ }
}

/**
 * Les opérations dont la CIBLE est la fiche cliente elle-même : leur
 * identifiant vit dans `data.id`, pas dans `data.client_id`.
 */
const OPS_SUR_LA_FICHE = new Set(['upsert_client', 'delete_client']);

/**
 * Identifiant de la fiche cliente concernée par cette opération, quel que
 * soit son type. 2026-08-08 : `contexteEchec` ne regardait que
 * `data.client_id`, donc un `upsert_client` visant une fiche tombstonée
 * n'était JAMAIS reconnu comme définitif — il repartait en file.
 */
function ficheVisee(op) {
  if (!op || !op.data) return null;
  if (OPS_SUR_LA_FICHE.has(op.type)) return op.data.id || null;
  return op.data.client_id || null;
}

/**
 * Contexte de classification d'un échec : la cible de cette opération
 * est-elle définitivement supprimée ? Seul un tombstone permet de l'affirmer.
 */
function contexteEchec(op) {
  const cible = ficheVisee(op);
  return { cibleDefinitivementSupprimee: Boolean(cible) && getTombstones().has(cible) };
}

/**
 * Cette opération viserait-elle une fiche définitivement supprimée ?
 *
 * Contrôle posé AVANT l'envoi, pas après l'échec : une requête condamnée
 * n'a aucune raison de partir. Le serveur la refuserait (V97.180), mais
 * l'envoyer laisserait quand même une trace d'écriture tentée sur une fiche
 * que la praticienne a supprimée.
 */
function viseUneFicheSupprimee(op) {
  const cible = ficheVisee(op);
  return Boolean(cible) && getTombstones().has(cible);
}

/** Cette opération est-elle déjà bloquée ? (même type, même identifiant) */
function estEnQuarantaine(op) {
  if (!op || !op.data || op.data.id === undefined) return false;
  return getQuarantine().some((x) => x.type === op.type && x.data && x.data.id === op.data.id);
}

function mettreEnQuarantaine(op, motif, definitif) {
  const q = getQuarantine();
  // Meme regle que la file : une seule entree par cible.
  const cible = (x) => x.type === op.type && x.data && op.data && x.data.id === op.data.id;
  const entree = {
    ...op,
    motif,
    definitif,
    misEnQuarantaineLe: new Date().toISOString(),
    tentatives: (op.tentatives || 0) + 1,
  };
  const existante = q.find(cible);
  saveQuarantine(existante ? q.map((x) => (x === existante ? entree : x)) : [...q, entree]);
}

/**
 * Après une sauvegarde confirmée d'une consultation, retire ses anciennes
 * tentatives d'arrière-plan (file et quarantaine) : elles portent une version
 * périmée que le serveur refuserait de toute façon.
 */
function oublierSynchroEnAttenteConsultation(id) {
  if (!id) return;
  const vise = (x) => x?.type === 'upsert_nutrition_consultation' && x?.data?.id === id;
  const q = getQuarantine();
  if (q.some(vise)) saveQuarantine(q.filter((x) => !vise(x)));
  const file = getSyncQueue();
  if (file.some(vise)) saveSyncQueue(file.filter((x) => !vise(x)));
}

/** Contenu de la quarantaine, pour l'affichage et l'export. */
export function getQuarantaine() {
  return getQuarantine();
}

/** Résumé lisible pour la praticienne (aucune donnée métier). */
export function getResumeQuarantaine() {
  return resumerQuarantaine(getQuarantine());
}

/**
 * Renvoie la quarantaine en JSON, pour sauvegarde avant toute décision.
 * Rien n'est supprimé par cet appel.
 */
export function exporterQuarantaine() {
  return JSON.stringify(getQuarantine(), null, 2);
}

/**
 * Remet les éléments en quarantaine dans la file, pour rejeu — à utiliser
 * une fois la cause corrigée (cliente recréée, migration appliquée…).
 * Le compteur de tentatives est remis à zéro : c'est un geste conscient.
 */
export function rejouerQuarantaine() {
  const q = getQuarantine();
  if (q.length === 0) return 0;
  let file = getSyncQueue();
  for (const op of q) {
    const { motif, definitif, misEnQuarantaineLe, tentatives, ...propre } = op;
    file = fusionnerDansFile(file, propre, Date.now());
  }
  saveSyncQueue(file);
  saveQuarantine([]);
  return q.length;
}

// ─── Santé de la synchro (2026-07-19, revue Benoit) ───
// La synchro est fire-and-forget : sans indicateur, un échec silencieux
// est invisible jusqu'à la perte de données. On horodate chaque succès et
// on expose (file d'attente, dernier succès) pour l'afficher au Dashboard.
const LAST_SYNC_KEY = 'bfc_last_cloud_sync';

function recordCloudSyncOk() {
  try { localStorage.setItem(LAST_SYNC_KEY, new Date().toISOString()); } catch { /* no-op */ }
}

export function getSyncHealth() {
  let lastSyncAt = null;
  try { lastSyncAt = localStorage.getItem(LAST_SYNC_KEY); } catch { /* no-op */ }
  // V97.153 — la quarantaine est remontee SEPAREMENT : « en attente » et
  // « bloque » n'appellent pas la meme reaction. Un element en quarantaine
  // ne repartira jamais tout seul, il demande une decision.
  const quarantaine = getQuarantine();
  return {
    pending: getSyncQueue().length,
    lastSyncAt,
    quarantine: quarantaine.length,
    quarantineResume: resumerQuarantaine(quarantaine),
  };
}

// ─── Cloud sync (fire-and-forget after localStorage writes) ───

async function cloudSyncClient(client) {
  if (!isCloudEnabled) return;
  const ownerId = await getCurrentOwnerId();
  const { history, progression, massageSessions, ...rest } = client;
  const f = rest.form || {};
  const metrics = computeMetrics(f);
  const row = {
    id: rest.id,
    owner_id: ownerId,
    categorie: rest.categorie || 'online',
    prenom: rest.prenom || '',
    formule: rest.formule || '',
    langue: rest.langue || 'FR',
    status: rest.status || 'nouveau',
    form: f,
    custom_rate: f.customRate ? Number(f.customRate) : null,
    waist_cm: toNumOrNull(f.tourTaille),
    hip_cm: toNumOrNull(f.tourHanche),
    neck_cm: toNumOrNull(f.tourCou),
    chest_cm: toNumOrNull(f.tourPoitrine),
    arm_right_cm: toNumOrNull(f.tourBrasDroit),
    arm_left_cm: toNumOrNull(f.tourBrasGauche),
    thigh_right_cm: toNumOrNull(f.tourCuisseDroite),
    thigh_left_cm: toNumOrNull(f.tourCuisseGauche),
    calf_cm: toNumOrNull(f.tourMollet),
    body_fat_percent: round1OrNull(metrics.bodyFat),
    lean_mass_kg: round1OrNull(metrics.leanMass),
    bmr_kcal: metrics.bmr != null ? Math.round(metrics.bmr) : null,
    interview_notes: rest.interviewNotes || null,
    latest_sections: rest.latestSections || null,
    created_by: rest.createdBy || PRATICIENNE_PAR_DEFAUT,
    created_at: rest.createdAt || new Date().toISOString(),
    updated_at: rest.updatedAt || new Date().toISOString(),
    pack_type: rest.packType || null,
    pack_started_at: rest.packStartedAt || null,
    pack_schedule: rest.packSchedule || null,
    pack_started_at_confirmed: rest.packStartedAtConfirmed ?? false,
    // V94.66 : ref vers clients.id cote app cliente, pour matching robuste
    // App Store / Play Store (cf. fetchClientsStatus + publishToClientApp).
    staging_client_id: rest.stagingClientId || null,
    // Choix d'Anissa pour l'espace cliente, retenu avant qu'il existe.
    client_app_options: rest.clientAppOptions || null,
  };
  try {
    const { error } = await supabase.from('clients').upsert(row, { onConflict: 'id' });
    if (error) throw error;
  } catch (error) {
    console.warn('Cloud sync client failed:', error?.message || error);
    addToSyncQueue({ type: 'upsert_client', data: row });
    return false;
  }
  recordCloudSyncOk();
  return true;
}

function cloudDeleteClient(id) {
  if (!isCloudEnabled) return;
  // V97.2 : soft-delete via is_deleted=true au lieu de DELETE row.
  // Pourquoi : un hard DELETE ne propage pas aux autres devices
  // qui ont la cliente en localStorage. Au prochain pullFromCloud,
  // ils detectent "local-only client" et la re-pushent vers Supabase
  // (cloudSyncClient ligne ~614), ressuscitant la cliente partout.
  // Le soft-delete via column is_deleted partagee permet a tous les
  // devices de voir le tombstone et nettoyer leur localStorage.
  supabase.from('clients').update({
    is_deleted: true,
    deleted_at: new Date().toISOString(),
  }).eq('id', id).then(({ error }) => {
    if (error) {
      console.warn('Cloud soft-delete client failed:', error.message);
      addToSyncQueue({ type: 'delete_client', data: { id } });
    }
  });
}

async function cloudSyncGeneration(clientId, gen) {
  if (!isCloudEnabled) return;
  const ownerId = await getCurrentOwnerId();
  const row = {
    id: gen.id,
    owner_id: ownerId,
    client_id: clientId,
    date: gen.date,
    sections: gen.sections,
  };
  supabase.from('generations').upsert(row, { onConflict: 'id' }).then(({ error }) => {
    if (error) {
      console.warn('Cloud sync generation failed:', error.message);
      addToSyncQueue({ type: 'upsert_generation', data: row });
    }
  });
}

async function cloudSyncProgression(clientId, entry) {
  if (!isCloudEnabled) return;
  const ownerId = await getCurrentOwnerId();
  const row = {
    id: entry.id,
    owner_id: ownerId,
    client_id: clientId,
    date: entry.date,
    poids: entry.poids ? Number(entry.poids) : null,
    comment: entry.comment || '',
  };
  supabase.from('progression').upsert(row, { onConflict: 'id' }).then(({ error }) => {
    if (error) {
      addToSyncQueue({ type: 'upsert_progression', data: row });
    }
  });
}

function cloudDeleteProgression(entryId) {
  if (!isCloudEnabled) return;
  supabase.from('progression').delete().eq('id', entryId).then(({ error }) => {
    if (error) addToSyncQueue({ type: 'delete_progression', data: { id: entryId } });
  });
}

async function cloudSyncMassageSession(clientId, session) {
  if (!isCloudEnabled) return;
  const ownerId = await getCurrentOwnerId();
  const row = {
    id: session.id,
    owner_id: ownerId,
    client_id: clientId,
    date: session.date,
    zones_traitees: session.zonesTraitees || '',
    techniques: session.techniques || '',
    observations: session.observations || '',
    recommandations: session.recommandations || '',
  };
  supabase.from('massage_sessions').upsert(row, { onConflict: 'id' }).then(({ error }) => {
    if (error) addToSyncQueue({ type: 'upsert_massage_session', data: row });
  });
}

function cloudDeleteMassageSession(sessionId) {
  if (!isCloudEnabled) return;
  supabase.from('massage_sessions').delete().eq('id', sessionId).then(({ error }) => {
    if (error) addToSyncQueue({ type: 'delete_massage_session', data: { id: sessionId } });
  });
}

function normalizeFollowupWeek(value) {
  const week = Number(value);
  return Number.isInteger(week) && week >= 1 && week <= 6 ? week : null;
}

function serializeFollowupData(consultation) {
  const source = consultation?.followupData;
  const data = source && typeof source === 'object' && !Array.isArray(source)
    ? { ...source }
    : {};
  const immediateManualUpdate = data?._versionProvenance?.trigger === 'manual_program_update';
  const week = immediateManualUpdate ? 0 : normalizeFollowupWeek(consultation?.followupWeek);
  if (consultation?.isFollowup === true && (week !== null || immediateManualUpdate)) {
    data._followupWeek = week;
  }
  return Object.keys(data).length > 0 ? data : null;
}

async function cloudSyncNutritionConsultation(consultation, {
  requireConfirmation = false,
  externalExpectedRequestId = null,
} = {}) {
  // V97.156 — on n'ENVOIE PLUS une requete deja condamnee.
  //
  // Le garde-fou precedent ne verifiait la quarantaine qu'APRES l'echec :
  // les 30 requetes partaient quand meme a chaque `pullFromCloud`, elles
  // n'etaient simplement plus remises en file. Le bruit reseau et les 409
  // subsistaient donc entierement. On sort avant tout appel.
  //
  // 2026-10-11 — la quarantaine ne bloque QUE la synchro d'arrière-plan. Une
  // sauvegarde confirmée (geste d'Anissa, compare-and-set + relecture) part
  // toujours : sinon une vieille requête refusée pendant une panne rendait la
  // consultation insauvegardable pour toujours dans ce navigateur, sans qu'un
  // seul appel n'atteigne la base (cas Guillemette).
  if (!requireConfirmation && consultation && consultation.id && estEnQuarantaine({
    type: 'upsert_nutrition_consultation',
    data: { id: consultation.id },
  })) {
    return null;
  }

  // Un plan issu d'un dossier externe ne s'écrit que par ses RPC
  // compare-and-set, avec la version attendue : un envoi d'arrière-plan sans
  // elle est refusé à chaque fois et finissait en quarantaine.
  const overrides = consultation?.editorial_overrides || consultation?.editorialOverrides;
  if (!requireConfirmation
      && overrides?.external_plan_input?.plan_input_mode === 'external_anonymized_dossier_v1') {
    return null;
  }

  const usesRevisionedFollowupContract = consultation?.followupData
    ?._versionProvenance?.kind === 'followup_manual_version';
  if (usesRevisionedFollowupContract && !requireConfirmation) {
    // Le trigger V97.202 interdit volontairement les UPSERT directs de ces
    // brouillons. Leur révision canonique doit revenir de la RPC CAS avant
    // toute adoption locale, ce qu'un fire-and-forget ne peut pas garantir.
    return null;
  }

  if (!isCloudEnabled) {
    if (requireConfirmation) throw new Error('Cloud indisponible : sauvegarde non confirmÃ©e.');
    return null;
  }
  const ownerId = await getCurrentOwnerId();

  // V85.3.2 FIX : la table nutrition_consultations a une FK sur clients.id.
  // Si le client n'est pas encore en cloud (cree offline, queue de sync bloquee,
  // changement de session, etc), l'upsert de la consultation echoue avec
  // nutrition_consultations_client_id_fkey — et toutes les modifications
  // (Remplacer IA, autosave, Sauvegarder) restent locales : au reload, Supabase
  // renvoie une version obsolete ou rien et le travail semble perdu.
  // Correctif : pousser le client AVANT la consultation pour garantir la FK.
  if (consultation.clientId) {
    const localClient = getClient(consultation.clientId);
    if (localClient) {
      try {
        const { data: existing } = await supabase
          .from('clients')
          .select('id')
          .eq('id', consultation.clientId)
          .maybeSingle();
        // 2026-08-08 — MÊME ARBITRAGE QUE `pullFromCloud`.
        //
        // Ce pré-upsert existe pour satisfaire la clé étrangère quand la
        // cliente n'est pas encore montée. Mais « pas encore montée » et
        // « supprimée définitivement » se ressemblent exactement d'ici : sans
        // arbitrage, sauvegarder une consultation restée en local suffirait à
        // recréer une fiche supprimée. On applique donc le même discriminant.
        const decisionPrealable = existing ? null : deciderPousseeClient({
          tombstone: getTombstones().has(consultation.clientId),
          dejaVuEnLigne: dejaVuEnLigne(consultation.clientId),
          lectureSuspecte: false,
        });
        if (decisionPrealable && !decisionPrealable.autorisee) {
          console.warn(
            `[sync] pre-upsert de la fiche refuse avant consultation — ${decisionPrealable.explication}`,
          );
        }

        if (!existing && decisionPrealable && decisionPrealable.autorisee) {
          // Client absent en cloud — on le pousse en preliminaire (synchrone ici).
          const { history, progression, massageSessions, ...rest } = localClient;
          const cf = rest.form || {};
          const cm = computeMetrics(cf);
          const clientRow = {
            id: rest.id,
            owner_id: ownerId,
            categorie: rest.categorie || 'online',
            prenom: rest.prenom || '',
            formule: rest.formule || '',
            langue: rest.langue || 'FR',
            status: rest.status || 'nouveau',
            form: cf,
            custom_rate: cf.customRate ? Number(cf.customRate) : null,
            waist_cm: toNumOrNull(cf.tourTaille),
            hip_cm: toNumOrNull(cf.tourHanche),
            neck_cm: toNumOrNull(cf.tourCou),
            chest_cm: toNumOrNull(cf.tourPoitrine),
            arm_right_cm: toNumOrNull(cf.tourBrasDroit),
            arm_left_cm: toNumOrNull(cf.tourBrasGauche),
            thigh_right_cm: toNumOrNull(cf.tourCuisseDroite),
            thigh_left_cm: toNumOrNull(cf.tourCuisseGauche),
            calf_cm: toNumOrNull(cf.tourMollet),
            body_fat_percent: round1OrNull(cm.bodyFat),
            lean_mass_kg: round1OrNull(cm.leanMass),
            bmr_kcal: cm.bmr != null ? Math.round(cm.bmr) : null,
            interview_notes: rest.interviewNotes || null,
            latest_sections: rest.latestSections || null,
            created_by: rest.createdBy || PRATICIENNE_PAR_DEFAUT,
            created_at: rest.createdAt || new Date().toISOString(),
            updated_at: rest.updatedAt || new Date().toISOString(),
            pack_type: rest.packType || null,
            pack_started_at: rest.packStartedAt || null,
            pack_schedule: rest.packSchedule || null,
            pack_started_at_confirmed: rest.packStartedAtConfirmed ?? false,
            staging_client_id: rest.stagingClientId || null,
          };
          const { error: clientErr } = await supabase
            .from('clients')
            .upsert(clientRow, { onConflict: 'id' });
          if (clientErr) {
            console.warn('Pre-upsert client before consultation failed:', clientErr.message);
          }
        }
      } catch {
        // Silently continue — le upsert consultation va s'executer et rapportera
        // l'erreur FK reelle si le client n'est toujours pas en cloud.
      }
    }
  }

  const row = {
    id: consultation.id,
    owner_id: ownerId,
    client_id: consultation.clientId,
    consultant_name: consultation.consultantName || 'Anissa',
    ...(consultation?.followupData?._versionProvenance?.trigger === 'manual_program_update'
      ? { label: consultation.label || '' }
      : {}),
    date: consultation.date,
    observations: consultation.observations || '',
    blood_test_done: consultation.bloodTestDone || false,
    dna_test_done: consultation.dnaTestDone || false,
    nutritional_observations: consultation.nutritionalObservations || '',
    nutrition_plan: consultation.nutritionPlan || '',
    mgd_plan_adoption_id: consultation.mgdPlanAdoptionId || null,
    supplements: consultation.supplements || '',
    recipes: consultation.recipes || '',
    fiche_frigo_json: consultation.ficheFrigoJson || null,
    lab_results: consultation.labResults || null,
    genetic_results: consultation.geneticResults || null,
    ai_analysis: consultation.aiAnalysis || null,
    notes_for_coach: consultation.notesForCoach || '',
    private_notes: consultation.privateNotes || '',
    is_followup: consultation.isFollowup || false,
    followup_data: serializeFollowupData(consultation),
    previous_consultation_id: consultation.previousConsultationId || null,
    followup_edit_revision: Number.isInteger(consultation.followupEditRevision)
      ? consultation.followupEditRevision
      : 0,
    mgd_recommendation: consultation.mgdRecommendation || 'none',
    mgd_recommended_tests_text: consultation.mgdRecommendedTestsText || '',
    status: consultation.status || 'questionnaire_recu',
    // V78 : propage l'etat soft delete cote DB
    is_deleted: consultation.isDeleted || false,
    deleted_at: consultation.deletedAt || null,
    deleted_by: consultation.deletedBy || null,
    created_at: consultation.createdAt || new Date().toISOString(),
    // V94.59 : composantes app cliente (lettre IA + recettes IA).
    // Necessite 2 colonnes JSONB dans nutrition_consultations Supabase :
    //   alter table nutrition_consultations add column intro_letter jsonb;
    //   alter table nutrition_consultations add column meal_recipes jsonb;
    intro_letter: consultation.intro_letter || null,
    meal_recipes: consultation.meal_recipes || null,
    // V97.13.41 : champ generique editorial_overrides pour les edits manuels
    // par section (Phase A3 option C). Structure :
    //   { strategy: { pillars: [{ title, description }] },
    //     supplements_intro: "...", ... }
    // Necessite migration Supabase :
    //   alter table nutrition_consultations add column editorial_overrides jsonb;
    editorial_overrides: consultation.editorial_overrides || null,
    // V97.17 Phase A : timeline therapeutique 5 phases (microbiote etc.).
    // Cf migration V97.17_protocol_phases.sql + service protocolPhases.js.
    protocol_phases: consultation.protocol_phases || null,
    active_phase_id: consultation.active_phase_id || null,
    client_app_publication: consultation.client_app_publication || null,
  };
  const handleSyncError = (error) => {
    if (!error) return null;
    const op = { type: 'upsert_nutrition_consultation', data: row };
    // V97.155 — le chemin DIRECT classe lui aussi l'echec. Sans ca, une
    // erreur definitive repartait en file a chaque appel, et `pullFromCloud`
    // en declenche un par consultation locale absente du cloud, a chaque
    // connexion. C'est exactement ce qui produisait les 30 avertissements
    // repetes.
    const { decision, motif, definitif } = classerEchec(error, 0, contexteEchec(op));
    if (decision === DECISION_ECHEC.QUARANTAINE) {
      if (!estEnQuarantaine(op)) {
        mettreEnQuarantaine(op, motif, definitif);
        // eslint-disable-next-line no-console
        console.warn(
          `[sync] consultation mise en quarantaine — ${motif}. `
          + 'Donnee conservee, exportable et rejouable.',
        );
      }
      if (requireConfirmation) throw new Error('Sauvegarde cloud refusÃ©e : donnÃ©e mise en quarantaine.');
      return null;
    }
    // eslint-disable-next-line no-console
    console.warn('Cloud sync nutrition consultation failed:', error.message);
    addToSyncQueue(op);
    if (requireConfirmation) throw new Error('Sauvegarde cloud non confirmÃ©e. RÃ©essaie avant de valider le plan.');
    return null;
  };

  if (requireConfirmation) {
    const confirmed = await persistNutritionConsultationCloudConfirmed({
      db: supabase,
      row,
      externalExpectedRequestId,
    });
    // La version canonique est en base : les anciennes tentatives
    // d'arrière-plan de cette consultation sont périmées.
    oublierSynchroEnAttenteConsultation(row.id);
    return confirmed;
  }

  void persistNutritionConsultationCloudConfirmed({ db: supabase, row })
    .catch((error) => { handleSyncError(error); });
  return null;
}

export async function saveApiKeyToCloud(apiKey) {
  if (!isCloudEnabled || !apiKey) return;
  await supabase
    .from('app_config')
    .upsert({ key: 'anthropic_api_key', value: apiKey });
}

export async function loadApiKeyFromCloud() {
  if (!isCloudEnabled) return null;
  const { data } = await supabase
    .from('app_config')
    .select('value')
    .eq('key', 'anthropic_api_key')
    .single();
  return data?.value || null;
}

export async function forceSyncAllConsultations() {
  if (!isCloudEnabled) return { synced: 0, errors: 0 };
  const ownerId = await getCurrentOwnerId();
  const consultations = readNutritionConsultations();
  let synced = 0, errors = 0, ignores = 0;
  for (const c of consultations) {
    // V97.156 — meme regle que la synchro automatique : une consultation en
    // quarantaine n'est pas renvoyee. Elle est comptee a part, pour que le
    // bouton dise la verite plutot que d'afficher des echecs connus.
    if (estEnQuarantaine({ type: 'upsert_nutrition_consultation', data: { id: c.id } })) {
      ignores++;
      continue;
    }
    const row = {
      id: c.id,
      owner_id: ownerId,
      client_id: c.clientId,
      consultant_name: c.consultantName || 'Anissa',
      ...(c?.followupData?._versionProvenance?.trigger === 'manual_program_update'
        ? { label: c.label || '' }
        : {}),
      date: c.date,
      observations: c.observations || '',
      blood_test_done: c.bloodTestDone || false,
      dna_test_done: c.dnaTestDone || false,
      nutritional_observations: c.nutritionalObservations || '',
      nutrition_plan: c.nutritionPlan || '',
      mgd_plan_adoption_id: c.mgdPlanAdoptionId || null,
      supplements: c.supplements || '',
      recipes: c.recipes || '',
      fiche_frigo_json: c.ficheFrigoJson || null,
      lab_results: c.labResults || null,
      genetic_results: c.geneticResults || null,
      ai_analysis: c.aiAnalysis || null,
      notes_for_coach: c.notesForCoach || '',
      private_notes: c.privateNotes || '',
      is_followup: c.isFollowup || false,
      followup_data: serializeFollowupData(c),
      previous_consultation_id: c.previousConsultationId || null,
      mgd_recommendation: c.mgdRecommendation || 'none',
      mgd_recommended_tests_text: c.mgdRecommendedTestsText || '',
      status: c.status || 'questionnaire_recu',
      label: c.label || null,
      created_at: c.createdAt || new Date().toISOString(),
      // V94.59 : composantes app cliente
      intro_letter: c.intro_letter || null,
      meal_recipes: c.meal_recipes || null,
      // V97.17.7.4 — Bugfix : forceSyncAllConsultations oubliait editorial_overrides
      // et protocol_phases. Au sync force, les phases configurees etaient
      // ecrasees par null cote cloud puis re-pull effacait le local.
      editorial_overrides: c.editorial_overrides || null,
      protocol_phases: c.protocol_phases || null,
      active_phase_id: c.active_phase_id || null,
    };
    // V97.80 : tampon inclus seulement s'il existe (evite de casser la sync
    // avant que la migration client_app_publication ne soit appliquee).
    if (c.client_app_publication) {
      row.client_app_publication = c.client_app_publication;
    }
    try {
      await cloudSyncNutritionConsultation(c, { requireConfirmation: true });
      synced++;
    } catch (error) {
      console.error('Sync failed:', c.id, error.message);
      errors++;
    }
  }
  if (errors === 0) recordCloudSyncOk();
  return { synced, errors, ignores };
}

// ─── Full cloud pull (on login / app start) ───

export async function pullFromCloud() {
  if (!isCloudEnabled) return { synced: false };

  try {
    const [clientsRes, gensRes, sessionsRes, progRes, nutritionRes] = await Promise.all([
      supabase.from('clients').select('*'),
      supabase.from('generations').select('*'),
      supabase.from('massage_sessions').select('*'),
      supabase.from('progression').select('*'),
      supabase.from('nutrition_consultations').select('*').then(r => r).catch(() => ({ data: [], error: null })),
    ]);

    if (clientsRes.error) throw clientsRes.error;

    // V97.2 : split cloud clients en actifs vs tombstones (is_deleted=true).
    // Les tombstones sont propages aux deletedIds locaux pour que tous les
    // devices nettoient leur localStorage et arretent de re-pusher.
    const allCloudClients = clientsRes.data || [];
    const cloudClients = allCloudClients.filter(c => !c.is_deleted);
    const cloudTombstones = allCloudClients.filter(c => c.is_deleted);
    for (const t of cloudTombstones) {
      addDeletedId(t.id);
    }
    // 2026-08-08 — On retient QUE ces identifiants ont été vus en ligne, y
    // compris ceux en corbeille. C'est ce qui permettra, plus bas, de ne pas
    // confondre « fiche neuve jamais synchronisée » et « fiche supprimée
    // ailleurs ». Sans cette mémoire, les deux cas sont indiscernables et
    // `pullFromCloud` repousse les deux.
    marquerVusEnLigne(allCloudClients.map((c) => c.id));
    const cloudGens = gensRes.data || [];
    const cloudSessions = sessionsRes.data || [];
    const cloudProg = progRes.data || [];
    const cloudNutrition = nutritionRes.data || [];

    // Group related data by client_id
    const gensByClient = {};
    for (const g of cloudGens) {
      if (!gensByClient[g.client_id]) gensByClient[g.client_id] = [];
      gensByClient[g.client_id].push({ id: g.id, date: g.date, sections: g.sections });
    }

    const sessionsByClient = {};
    for (const s of cloudSessions) {
      if (!sessionsByClient[s.client_id]) sessionsByClient[s.client_id] = [];
      sessionsByClient[s.client_id].push({
        id: s.id, date: s.date,
        zonesTraitees: s.zones_traitees, techniques: s.techniques,
        observations: s.observations, recommandations: s.recommandations,
      });
    }

    const progByClient = {};
    for (const p of cloudProg) {
      if (!progByClient[p.client_id]) progByClient[p.client_id] = [];
      progByClient[p.client_id].push({
        id: p.id, date: p.date,
        poids: p.poids, comment: p.comment,
      });
    }

    // Store nutrition consultations locally
    const localNutrition = cloudNutrition.map(n => ({
      id: n.id,
      clientId: n.client_id,
      consultantName: n.consultant_name || 'Anissa',
      label: n.label || '',
      date: n.date,
      observations: n.observations || '',
      bloodTestDone: n.blood_test_done || false,
      dnaTestDone: n.dna_test_done || false,
      nutritionalObservations: n.nutritional_observations || '',
      nutritionPlan: n.nutrition_plan || '',
      mgdPlanAdoptionId: n.mgd_plan_adoption_id || null,
      supplements: n.supplements || '',
      recipes: n.recipes || '',
      ficheFrigoJson: n.fiche_frigo_json || null,
      labResults: n.lab_results || {},
      geneticResults: n.genetic_results || {},
      aiAnalysis: n.ai_analysis || null,
      notesForCoach: n.notes_for_coach || '',
      privateNotes: n.private_notes || '',
      isFollowup: n.is_followup || false,
      followupData: n.followup_data || null,
      followupWeek: normalizeFollowupWeek(n.followup_data?._followupWeek),
      previousConsultationId: n.previous_consultation_id || null,
      followupEditRevision: Number.isInteger(n.followup_edit_revision)
        ? n.followup_edit_revision
        : 0,
      mgdRecommendation: n.mgd_recommendation || 'none',
      mgdRecommendedTestsText: n.mgd_recommended_tests_text || '',
      status: n.status || 'questionnaire_recu',
      // V78 : propage l'etat soft delete depuis le cloud
      isDeleted: n.is_deleted || false,
      deletedAt: n.deleted_at || null,
      deletedBy: n.deleted_by || null,
      // V94.59 : composantes app cliente (lettre IA + recettes IA)
      intro_letter: n.intro_letter || null,
      meal_recipes: n.meal_recipes || null,
      // V97.17.7.4 — BUGFIX critique : le mapping cloud→local oubliait
      // editorial_overrides + protocol_phases + active_phase_id. Au refresh
      // page, pullFromCloud ecrasait le localStorage avec une version sans
      // ces champs → Anissa perdait son parcours therapeutique et devait le
      // re-configurer. Cause : ajout des colonnes en V97.13.41 et V97.17.0
      // sans toucher pullFromCloud (oubli pipeline).
      editorial_overrides: n.editorial_overrides || null,
      protocol_phases: n.protocol_phases || null,
      active_phase_id: n.active_phase_id || null,
      // V97.80 : tampon de publication app cliente par version (statut frise).
      client_app_publication: n.client_app_publication || null,
      createdAt: n.created_at,
    }));
    // Merge with existing local nutrition consultations
    // V78 : utiliser la lecture RAW (sans filtre) pour le merge — sinon les soft-delete
    // locaux seraient invisibles et pourraient etre ecrases par les versions cloud.
    const existingNutrition = readNutritionConsultationsRaw();
    const nutritionMap = {};
    // Partir du cloud
    for (const n of localNutrition) nutritionMap[n.id] = n;
    // Merger avec le local : garder le plus récent par createdAt
    for (const n of existingNutrition) {
      if (!nutritionMap[n.id]) {
        nutritionMap[n.id] = n;
      } else {
        const cloudDate = new Date(nutritionMap[n.id].createdAt || 0);
        const localDate = new Date(n.createdAt || 0);
        if (localDate > cloudDate) {
          nutritionMap[n.id] = n;
        }
      }
    }
    writeNutritionConsultations(Object.values(nutritionMap));

    // Build cloud client objects in localStorage format
    const cloudMap = {};
    for (const c of cloudClients) {
      const history = (gensByClient[c.id] || []).sort((a, b) => new Date(b.date) - new Date(a.date));
      const progression = (progByClient[c.id] || []).sort((a, b) => new Date(a.date) - new Date(b.date));
      const massageSessions = (sessionsByClient[c.id] || []).sort((a, b) => new Date(b.date) - new Date(a.date));
      cloudMap[c.id] = {
        id: c.id,
        categorie: c.categorie || 'online',
        prenom: c.prenom || '',
        formule: c.formule || '',
        langue: c.langue || 'FR',
        status: c.status || 'nouveau',
        form: c.form || {},
        latestSections: c.latest_sections || null,
        interviewNotes: c.interview_notes || null,
        createdBy: c.created_by || PRATICIENNE_PAR_DEFAUT,
        createdAt: c.created_at,
        updatedAt: c.updated_at,
        packType: c.pack_type || null,
        packStartedAt: c.pack_started_at || null,
        packSchedule: c.pack_schedule || null,
        packStartedAtConfirmed: c.pack_started_at_confirmed ?? false,
        stagingClientId: c.staging_client_id || null,
        clientAppOptions: c.client_app_options || null,
        history,
        progression,
        massageSessions,
      };
    }

    // Merge with localStorage
    const localClients = readAll();
    const localMap = {};
    for (const c of localClients) {
      localMap[c.id] = c;
    }

    const merged = {};
    const deletedIds = readDeletedIds();

    // All cloud clients (skip tombstoned IDs — intentionally deleted)
    for (const [id, cloudClient] of Object.entries(cloudMap)) {
      if (deletedIds.has(id)) continue;
      const local = localMap[id];
      if (!local) {
        merged[id] = cloudClient;
      } else {
        // Keep the newer one
        const cloudDate = new Date(cloudClient.updatedAt || 0);
        const localDate = new Date(local.updatedAt || 0);
        if (cloudDate >= localDate) {
          merged[id] = {
            ...cloudClient,
            history: mergeArraysById(cloudClient.history, local.history),
            progression: mergeArraysById(cloudClient.progression, local.progression),
            massageSessions: mergeArraysById(cloudClient.massageSessions, local.massageSessions),
          };
        } else {
          merged[id] = {
            ...local,
            history: mergeArraysById(local.history, cloudClient.history),
            progression: mergeArraysById(local.progression, cloudClient.progression),
            massageSessions: mergeArraysById(local.massageSessions, cloudClient.massageSessions),
          };
        }
      }
    }

    // Local-only clients (not in cloud) — skip tombstoned
    for (const [id, local] of Object.entries(localMap)) {
      if (!cloudMap[id] && !deletedIds.has(id)) {
        merged[id] = local;
      }
    }

    const mergedList = Object.values(merged);
    writeAll(mergedList);

    // V97.157 — CONSULTER LE REGISTRE DES SUPPRESSIONS AVANT DE REMONTER.
    //
    // Une cliente definitivement purgee laisse un tombstone technique. Toute
    // donnee locale qui s'y rattache doit partir en quarantaine AVANT la
    // moindre tentative d'envoi : sans ca, un appareil desynchronise
    // pousserait des donnees deja supprimees volontairement, et se ferait
    // refuser en boucle. La quarantaine conserve la donnee, elle ne la
    // supprime jamais.
    //
    // Tolerant a l'absence de la table : tant que la migration V97.157 n'est
    // pas appliquee, on continue sans registre, sans casser la synchro.
    //
    // ⚠ 2026-08-08 — CE BLOC ÉTAIT PLACÉ *APRÈS* LA POUSSÉE DES FICHES.
    // Le registre des suppressions était donc lu trop tard pour protéger les
    // fiches elles-mêmes : il n'arbitrait que les consultations. Il est
    // remonté ici, avant toute écriture.
    let idsSupprimes = new Set();
    try {
      const { data: tombstones } = await supabase
        .from('entites_supprimees')
        .select('id_entite')
        .eq('type_entite', 'client');
      idsSupprimes = new Set((tombstones || []).map((t) => t.id_entite));
      saveTombstones(idsSupprimes);
    } catch { /* registre absent : on n'en tient pas compte */ }

    // ─── Poussée des données locales absentes de la base ──────────────────
    //
    // 2026-08-08 — C'EST ICI QUE LES TROIS FICHES SUPPRIMÉES SONT REVENUES.
    //
    // Le test « absente de la base » ne distingue pas une fiche neuve créée
    // hors ligne d'une fiche supprimée définitivement ailleurs. Les deux sont
    // absentes ; seule la première doit partir. Le discriminant est confié à
    // `deciderPousseeClient` : jamais vue en ligne → création ; déjà vue puis
    // disparue → rejeu, refusé. Rien n'est supprimé localement : on refuse
    // d'écrire, la donnée reste consultable et exportable.
    const lectureSuspecte = lectureEstSuspecte(allCloudClients.length, mergedList.length);
    if (lectureSuspecte) {
      console.warn(
        '[sync] la base ne rend aucune fiche alors que le local en porte '
        + `${mergedList.length} : aucune poussee tant que l'etat distant est incertain.`,
      );
    }
    let rejeuxRefuses = 0;
    for (const client of mergedList) {
      if (cloudMap[client.id] || deletedIds.has(client.id)) continue;

      const decision = deciderPousseeClient({
        tombstone: idsSupprimes.has(client.id),
        dejaVuEnLigne: dejaVuEnLigne(client.id),
        lectureSuspecte,
      });
      if (!decision.autorisee) {
        rejeuxRefuses += 1;
        console.warn(`[sync] poussee refusee pour une fiche — ${decision.explication}`);
        continue;
      }

      cloudSyncClient(client);
      for (const gen of (client.history || [])) {
        cloudSyncGeneration(client.id, gen);
      }
      for (const prog of (client.progression || [])) {
        cloudSyncProgression(client.id, prog);
      }
      for (const session of (client.massageSessions || [])) {
        cloudSyncMassageSession(client.id, session);
      }
    }
    if (rejeuxRefuses > 0) {
      console.warn(`[sync] ${rejeuxRefuses} rejeu(x) de fiche refuse(s) — aucune resurrection.`);
    }

    // Push local-only nutrition consultations to cloud
    const cloudNutritionIds = new Set(cloudNutrition.map(n => n.id));
    for (const n of Object.values(nutritionMap)) {
      if (cloudNutritionIds.has(n.id)) continue;
      if (n.clientId && idsSupprimes.has(n.clientId)) {
        // Cliente definitivement supprimee : on ne tente meme pas.
        const op = { type: 'upsert_nutrition_consultation', data: { id: n.id } };
        if (!estEnQuarantaine(op)) {
          mettreEnQuarantaine(op, 'cliente definitivement supprimee (registre serveur)', true);
        }
        continue;
      }
      cloudSyncNutritionConsultation(n);
    }

    return { synced: true, count: mergedList.length };
  } catch (err) {
    console.warn('Pull from cloud failed:', err.message);
    return { synced: false, error: err.message };
  }
}

function mergeArraysById(a = [], b = []) {
  const map = {};
  for (const item of a) map[item.id] = item;
  for (const item of b) {
    if (!map[item.id]) map[item.id] = item;
  }
  return Object.values(map);
}

// ─── Retry queued operations ───

export async function retrySyncQueue() {
  if (!isCloudEnabled) return;
  const queue = getSyncQueue();
  if (queue.length === 0) return;

  // V97.154 — hors ligne, on ne rejoue meme pas : chaque appel echouerait au
  // transport et, sans cette garde, finirait par consommer le plafond de
  // tentatives sur des donnees valides. Le retour en ligne declenche deja un
  // nouveau rejeu (App.jsx, evenement `online`).
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return;

  const remaining = [];
  for (const op of queue) {
    // 2026-08-08 — UNE ÉCRITURE DÉJÀ CONDAMNÉE NE PART PAS.
    //
    // `retrySyncQueue` est le seul chemin qui rejoue `upsert_client` ET
    // `upsert_generation` : il pouvait à lui seul recréer une fiche et sa
    // génération. Le serveur les refuse désormais (V97.180), mais une requête
    // qu'on sait condamnée n'a pas à partir. Elle va en quarantaine, donc la
    // donnée reste conservée, consultable et rejouable si le tombstone est
    // levé volontairement.
    if ((op.type === 'upsert_client' || op.type === 'upsert_generation') && viseUneFicheSupprimee(op)) {
      if (!estEnQuarantaine(op)) {
        mettreEnQuarantaine(op, 'fiche definitivement supprimee — rejeu refuse avant envoi', true);
      }
      // eslint-disable-next-line no-console
      console.warn(
        `[sync] ${op.type} non envoye : la fiche visee est definitivement supprimee. `
        + 'La donnee est conservee en quarantaine.',
      );
      continue;
    }

    let error = null;
    switch (op.type) {
      case 'upsert_client':
        ({ error } = await supabase.from('clients').upsert(op.data, { onConflict: 'id' }));
        break;
      case 'delete_client':
        ({ error } = await supabase.from('clients').delete().eq('id', op.data.id));
        if (!error) addDeletedId(op.data.id);
        break;
      case 'upsert_generation':
        ({ error } = await supabase.from('generations').upsert(op.data, { onConflict: 'id' }));
        break;
      case 'upsert_progression':
        ({ error } = await supabase.from('progression').upsert(op.data, { onConflict: 'id' }));
        break;
      case 'delete_progression':
        ({ error } = await supabase.from('progression').delete().eq('id', op.data.id));
        break;
      case 'upsert_massage_session':
        ({ error } = await supabase.from('massage_sessions').upsert(op.data, { onConflict: 'id' }));
        break;
      case 'delete_massage_session':
        ({ error } = await supabase.from('massage_sessions').delete().eq('id', op.data.id));
        break;
      case 'upsert_nutrition_consultation':
        try {
          await persistNutritionConsultationCloudConfirmed({ db: supabase, row: op.data });
          error = null;
        } catch (syncError) {
          error = syncError;
        }
        break;
    }
    if (!error) continue;

    // V97.153 — on CLASSE l'echec au lieu de tout remettre en file.
    //
    // Avant : `if (error) remaining.push(op)`. Une violation de cle
    // etrangere, qu'aucun rejeu ne resoudra, revenait indefiniment. 30
    // consultations orphelines ont ainsi ete rejouees a chaque demarrage
    // pendant une semaine, noyant les vraies erreurs.
    const dejaTentees = op.tentatives || 0;
    const { decision, motif, definitif, compteTentative } = classerEchec(error, dejaTentees, contexteEchec(op));
    if (decision === DECISION_ECHEC.QUARANTAINE) {
      mettreEnQuarantaine(op, motif, definitif);
      // eslint-disable-next-line no-console
      console.warn(
        `[sync] operation ${op.type} mise en quarantaine — ${motif}. `
        + 'La donnee est conservee et reste rejouable.',
      );
      continue; // les autres elements continuent de se synchroniser
    }
    // Une panne reseau ne consomme pas de tentative (compteTentative=false).
    remaining.push({ ...op, tentatives: compteTentative ? dejaTentees + 1 : dejaTentees });
  }
  saveSyncQueue(remaining);
}

// ─── Public API (same signatures as before) ───

export function getClients() {
  // V97.136 — Ne JAMAIS exposer une cliente supprimée. `readAll()` renvoie le
  // localStorage brut, qui peut encore contenir une cliente supprimée (tombstone
  // pas encore nettoyé, ou suppression faite depuis un autre appareil). On filtre
  // ici, au point de lecture public (alertes, listes, compteurs) : registre des
  // suppressions (deletedIds) + flags soft-delete. Symétrique du filtre déjà en
  // place sur les consultations (readNutritionConsultations).
  const deletedIds = readDeletedIds();
  return readAll()
    .filter((c) => c && c.id && !deletedIds.has(c.id) && !c.isDeleted && !c.is_deleted && !c.deletedAt)
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
}

export function getClient(id) {
  return readAll().find(c => c.id === id) || null;
}

// Get clients shared with Anissa (formules qui donnent acces nutrition, created by Benoit)
// V86.3 : ajout pack20 + pack30 qui ont egalement acces optionnel aux services Anissa.
export const FORMULES_WITH_ANISSA_ACCESS = ['suivi', 'intensif', 'pack20', 'pack30'];
// 2026-08-08 — « partagée » voulait dire « créée par Benoit mais accessible à
// Anissa ». Ce cas n'existe plus : toutes les fiches sont à elle, donc cette
// fonction rend désormais une liste vide. Elle est CONSERVÉE parce qu'elle est
// encore passée en props à deux composants du tableau de bord d'Anissa ; la
// démonter est une passe à part, hors du retrait BenFitCoach.
export function getSharedClients() {
  return getClients().filter(c => {
    const formule = c.formule || '';
    return FORMULES_WITH_ANISSA_ACCESS.includes(formule)
      && (c.createdBy || PRATICIENNE_PAR_DEFAUT) !== 'anissa';
  });
}

// Get clients created by Anissa
export function getAnissaOwnClients() {
  return getClients().filter(c => c.createdBy === 'anissa');
}

// 2026-08-08 — getBenoitClients() retirée avec le cockpit BenFitCoach. Son seul
// appelant était ABCoachingSection, supprimé dans la même passe.

function saveClientLocally(client) {
  const clients = readAll();
  const idx = clients.findIndex(c => c.id === client.id);
  if (idx >= 0) {
    clients[idx] = { ...clients[idx], ...client, updatedAt: new Date().toISOString() };
  } else {
    clients.push({
      ...client,
      id: client.id || crypto.randomUUID(),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      status: 'nouveau',
      history: [],
      progression: [],
      massageSessions: [],
    });
  }
  writeAll(clients);
  return clients.find(c => c.id === client.id) || clients[clients.length - 1];
}

export function saveClient(client) {
  const saved = saveClientLocally(client);
  cloudSyncClient(saved);
  return saved;
}

/**
 * Sauvegarde utilisée avant de quitter une fiche clinique. En mode cloud, le
 * retour à la cliente n'est autorisé qu'après confirmation de Supabase ; une
 * écriture locale seule ne doit pas donner l'illusion que la correction est
 * disponible pour le préflight qui relira immédiatement le serveur.
 */
export async function saveClientConfirmed(client) {
  const saved = saveClientLocally(client);
  const staleFingerprints = pendingClientUpsertFingerprints(saved.id);
  const synced = await cloudSyncClient(saved);
  if (isCloudEnabled && synced !== true) {
    throw new Error('La sauvegarde cloud de la fiche n’a pas été confirmée. Réessaie avant de quitter.');
  }
  // Une version plus ancienne a pu être mise en file lors d'un premier
  // échec. Puisque Supabase vient de confirmer la version courante, son rejeu
  // ne doit jamais pouvoir l'écraser plus tard.
  clearPendingClientUpserts(saved.id, staleFingerprints);
  return saved;
}

export function deleteClient(id) {
  addDeletedId(id);
  writeAll(readAll().filter(c => c.id !== id));
  cloudDeleteClient(id);
}

export function addGeneration(clientId, sections) {
  const clients = readAll();
  const client = clients.find(c => c.id === clientId);
  if (!client) return;
  if (!client.history) client.history = [];
  const gen = {
    id: crypto.randomUUID(),
    date: new Date().toISOString(),
    sections,
  };
  client.history.unshift(gen);
  client.latestSections = sections;
  client.updatedAt = new Date().toISOString();
  if (client.status === 'nouveau') client.status = 'actif';
  writeAll(clients);
  cloudSyncClient(client);
  cloudSyncGeneration(clientId, gen);
  return client;
}

export function updateClientStatus(id, status) {
  const clients = readAll();
  const client = clients.find(c => c.id === id);
  if (!client) return;
  client.status = status;
  client.updatedAt = new Date().toISOString();
  writeAll(clients);
  cloudSyncClient(client);
}

export function updateInterviewNotes(clientId, interviewNotes) {
  const clients = readAll();
  const client = clients.find(c => c.id === clientId);
  if (!client) return null;
  client.interviewNotes = interviewNotes;
  client.updatedAt = new Date().toISOString();
  writeAll(clients);
  cloudSyncClient(client);
  return client;
}

export function updateClientSection(clientId, sectionTitle, content) {
  const clients = readAll();
  const client = clients.find(c => c.id === clientId);
  if (!client || !client.latestSections) return;
  client.latestSections[sectionTitle] = content;
  client.updatedAt = new Date().toISOString();
  writeAll(clients);
  cloudSyncClient(client);
}

// Progression tracking
export function addProgressionEntry(clientId, entry) {
  const clients = readAll();
  const client = clients.find(c => c.id === clientId);
  if (!client) return;
  if (!client.progression) client.progression = [];
  const progEntry = {
    id: crypto.randomUUID(),
    date: entry.date || new Date().toISOString().split('T')[0],
    poids: entry.poids,
    comment: entry.comment || '',
  };
  client.progression.push(progEntry);
  client.progression.sort((a, b) => new Date(a.date) - new Date(b.date));
  client.updatedAt = new Date().toISOString();
  writeAll(clients);
  cloudSyncProgression(clientId, progEntry);
  return client;
}

export function deleteProgressionEntry(clientId, entryId) {
  const clients = readAll();
  const client = clients.find(c => c.id === clientId);
  if (!client || !client.progression) return;
  client.progression = client.progression.filter(e => e.id !== entryId);
  client.updatedAt = new Date().toISOString();
  writeAll(clients);
  cloudDeleteProgression(entryId);
}

// Massage session notes
export function addMassageSession(clientId, session) {
  const clients = readAll();
  const client = clients.find(c => c.id === clientId);
  if (!client) return;
  if (!client.massageSessions) client.massageSessions = [];
  const sessionEntry = {
    id: crypto.randomUUID(),
    date: session.date || new Date().toISOString().split('T')[0],
    zonesTraitees: session.zonesTraitees || '',
    techniques: session.techniques || '',
    observations: session.observations || '',
    recommandations: session.recommandations || '',
  };
  client.massageSessions.unshift(sessionEntry);
  client.updatedAt = new Date().toISOString();
  writeAll(clients);
  cloudSyncMassageSession(clientId, sessionEntry);
  return client;
}

export function deleteMassageSession(clientId, sessionId) {
  const clients = readAll();
  const client = clients.find(c => c.id === clientId);
  if (!client || !client.massageSessions) return;
  client.massageSessions = client.massageSessions.filter(s => s.id !== sessionId);
  client.updatedAt = new Date().toISOString();
  writeAll(clients);
  cloudDeleteMassageSession(sessionId);
}

// ─── Nutrition Consultations ───

export function getNutritionConsultations(clientId) {
  return readNutritionConsultations()
    .filter(n => n.clientId === clientId)
    .sort((a, b) => new Date(b.date) - new Date(a.date));
}

export function buildNutritionConsultationEntry(consultation) {
  const immediateManualUpdate = consultation?.followupData?._versionProvenance?.trigger
    === 'manual_program_update';
  const entry = {
    id: consultation.id || crypto.randomUUID(),
    clientId: consultation.clientId,
    consultantName: consultation.consultantName || 'Anissa',
    label: consultation.label || '',
    date: consultation.date || new Date().toISOString(),
    observations: consultation.observations || '',
    bloodTestDone: consultation.bloodTestDone || false,
    dnaTestDone: consultation.dnaTestDone || false,
    nutritionalObservations: consultation.nutritionalObservations || '',
    nutritionPlan: consultation.nutritionPlan || '',
    mgdPlanAdoptionId: consultation.mgdPlanAdoptionId || null,
    supplements: consultation.supplements || '',
    recipes: consultation.recipes || '',
    notesForCoach: consultation.notesForCoach || '',
    privateNotes: consultation.privateNotes || '',
    ficheFrigoJson: consultation.ficheFrigoJson || null,
    labResults: consultation.labResults || {},
    geneticResults: consultation.geneticResults || {},
    aiAnalysis: consultation.aiAnalysis || null,
    aiDirectives: consultation.aiDirectives || '', // V96.19 — override texte libre par cliente
    isFollowup: consultation.isFollowup || false,
    followupData: consultation.followupData || null,
    followupWeek: immediateManualUpdate ? 0 : normalizeFollowupWeek(consultation.followupWeek),
    previousConsultationId: consultation.previousConsultationId || null,
    followupEditRevision: Number.isInteger(consultation.followupEditRevision)
      ? consultation.followupEditRevision
      : 0,
    mgdRecommendation: consultation.mgdRecommendation || 'none',
    mgdRecommendedTestsText: consultation.mgdRecommendedTestsText || '',
    status: consultation.status || 'questionnaire_recu',
    createdAt: consultation.createdAt || new Date().toISOString(),
    // V87.5 : tracabilite locale (FR/EN) pour audit et stats usage.
    // Aucune nouvelle colonne DB : stocke dans le champ ai_analysis existant
    // via la couche cloud OU lu a la racine de l'objet en local. Defaults a FR.
    planLocale: consultation.planLocale || 'FR',
    // V88 : couche de finalisation humaine au-dessus du plan IA.
    // finalText = version editee par Anissa (markdown brut), prime sur nutritionPlan
    // au moment du PDF si isFinal est true. Ne jamais ecraser nutritionPlan.
    finalText: consultation.finalText || null,
    isFinal: consultation.isFinal || false,
    finalUpdatedAt: consultation.finalUpdatedAt || null,
    // V88.12 : historique des versions finales. Archive a chaque save.
    // Tableau de { text, createdAt }. Limite a 15 entrees via slice(-15).
    finalVersions: Array.isArray(consultation.finalVersions) ? consultation.finalVersions : [],
    // V94.59 : composantes app cliente persistees. Etaient ignorees du
    // whitelist du saveNutritionConsultation → perte au reload (bug user).
    intro_letter: consultation.intro_letter || null,
    meal_recipes: consultation.meal_recipes || null,
    // V97.13.41 : champ generique pour les edits manuels par section
    // (Phase A3 option C). Voir cloudSyncNutritionConsultation pour la
    // migration Supabase requise.
    editorial_overrides: consultation.editorial_overrides || null,
    // V97.17 Phase A : timeline therapeutique 5 phases. Migration
    // V97.17_protocol_phases.sql sur Supabase.
    protocol_phases: consultation.protocol_phases || null,
    active_phase_id: consultation.active_phase_id || null,
    // V97.80 : tampon de publication app cliente par version (statut frise).
    // { published_at, effective_at, published_version, plan_status, stamped_at }.
    // Migration : alter table nutrition_consultations add column client_app_publication jsonb;
    client_app_publication: consultation.client_app_publication || null,
  };
  // V78 : si une consultation avec ce id existe deja et est soft-delete,
  // preserver le flag pour eviter un "undelete" silencieux via edit.
  const allRaw = readNutritionConsultationsRaw();
  const existing = allRaw.find(c => c.id === entry.id);
  if (existing?.isDeleted) {
    entry.isDeleted = existing.isDeleted;
    entry.deletedAt = existing.deletedAt;
    entry.deletedBy = existing.deletedBy;
  }
  return entry;
}

function writeNutritionConsultationEntry(entry) {
  const consultations = readNutritionConsultations();
  const idx = consultations.findIndex(c => c.id === entry.id);
  if (idx >= 0) {
    consultations[idx] = entry;
  } else {
    consultations.push(entry);
  }
  writeNutritionConsultations(consultations);
  return entry;
}

export function saveNutritionConsultation(consultation, { syncCloud = true } = {}) {
  if (consultation?.followupData?._versionProvenance?.kind === 'followup_manual_version') {
    throw new Error('Cette version de suivi exige une sauvegarde cloud confirmée.');
  }
  const entry = buildNutritionConsultationEntry(consultation);
  writeNutritionConsultationEntry(entry);
  if (syncCloud) void cloudSyncNutritionConsultation(entry);
  return entry;
}

export async function saveNutritionConsultationConfirmed(consultation, {
  externalExpectedRequestId = null,
} = {}) {
  const entry = buildNutritionConsultationEntry(consultation);
  const confirmation = await cloudSyncNutritionConsultation(entry, {
    requireConfirmation: true,
    externalExpectedRequestId,
  });
  const canonical = normalizeNutritionConsultationCloudRow(
    confirmation?.row,
    entry.clientId,
  );
  if (!canonical || canonical.id !== entry.id) {
    throw new Error('La version canonique sauvegardée est introuvable.');
  }
  const canonicalEntry = buildNutritionConsultationEntry(canonical);
  // Le local n'est adopté qu'après UPSERT + relecture exacte de la ligne
  // canonique. Un échec Supabase ne peut donc pas être présenté comme sauvé.
  return writeNutritionConsultationEntry(canonicalEntry);
}

// V78 : Soft delete d'une consultation nutrition.
// La ligne reste en localStorage + Supabase (is_deleted=true, deleted_at, deleted_by)
// mais disparait des lectures (filtre applique dans readNutritionConsultations).
export function softDeleteConsultation(consultationId, deletedBy = null) {
  if (!consultationId) return false;
  const all = readNutritionConsultationsRaw();
  const idx = all.findIndex(c => c.id === consultationId);
  if (idx < 0) return false;
  if (all[idx].isDeleted) return true; // idempotent
  all[idx] = {
    ...all[idx],
    isDeleted: true,
    deletedAt: new Date().toISOString(),
    deletedBy: deletedBy || null,
  };
  writeNutritionConsultations(all);
  cloudSyncNutritionConsultation(all[idx]);
  return true;
}

// ─── Client reminder frequency (localStorage, default 3 months) ───

const REMINDER_FREQ_KEY = 'bfc_reminder_frequencies';
export const DEFAULT_REMINDER_MONTHS = 3;

function readReminderFrequencies() {
  try {
    return JSON.parse(localStorage.getItem(REMINDER_FREQ_KEY) || '{}');
  } catch {
    return {};
  }
}

function writeReminderFrequencies(data) {
  localStorage.setItem(REMINDER_FREQ_KEY, JSON.stringify(data));
}

export function setClientReminderFrequency(clientId, months) {
  if (!clientId) return;
  const data = readReminderFrequencies();
  const n = Number(months);
  if (!Number.isFinite(n) || n <= 0) {
    delete data[clientId];
  } else {
    data[clientId] = n;
  }
  writeReminderFrequencies(data);
}

export function getClientReminderFrequency(clientId) {
  if (!clientId) return DEFAULT_REMINDER_MONTHS;
  const data = readReminderFrequencies();
  const n = Number(data[clientId]);
  return Number.isFinite(n) && n > 0 ? n : DEFAULT_REMINDER_MONTHS;
}

// ─── Plan versioning (localStorage only, max 3 versions per client) ───

const PLAN_VERSIONS_KEY = 'bfc_plan_versions';
const MAX_PLAN_VERSIONS = 3;

function readPlanVersions() {
  try {
    return JSON.parse(localStorage.getItem(PLAN_VERSIONS_KEY) || '{}');
  } catch {
    return {};
  }
}

function writePlanVersions(data) {
  localStorage.setItem(PLAN_VERSIONS_KEY, JSON.stringify(data));
}

export function savePlanVersion(clientId, version) {
  if (!clientId || !version) return;
  const all = readPlanVersions();
  const list = Array.isArray(all[clientId]) ? all[clientId] : [];
  const entry = {
    id: crypto.randomUUID(),
    savedAt: new Date().toISOString(),
    nutritionPlan: version.nutritionPlan || '',
    supplements: version.supplements || '',
    recipes: version.recipes || '',
    ficheFrigoJson: version.ficheFrigoJson || null,
    label: version.label || '',
  };
  list.unshift(entry);
  all[clientId] = list.slice(0, MAX_PLAN_VERSIONS);
  writePlanVersions(all);
  return entry;
}

export function getPlanVersions(clientId) {
  if (!clientId) return [];
  const all = readPlanVersions();
  return Array.isArray(all[clientId]) ? all[clientId] : [];
}

export function deletePlanVersion(clientId, versionId) {
  if (!clientId || !versionId) return;
  const all = readPlanVersions();
  if (!Array.isArray(all[clientId])) return;
  all[clientId] = all[clientId].filter(v => v.id !== versionId);
  writePlanVersions(all);
}

// Export/Import
export function exportAllData() {
  return JSON.stringify({
    version: 1,
    exportedAt: new Date().toISOString(),
    clients: readAll(),
    nutritionConsultations: readNutritionConsultations(),
    apiKey: localStorage.getItem('bfc_api_key') || '',
  }, null, 2);
}

export function importAllData(jsonString) {
  const data = JSON.parse(jsonString);
  if (!data.clients || !Array.isArray(data.clients)) {
    throw new Error('Format invalide: pas de tableau clients');
  }
  writeAll(data.clients);
  if (data.nutritionConsultations) {
    writeNutritionConsultations(data.nutritionConsultations);
  }
  if (data.apiKey) {
    localStorage.setItem('bfc_api_key', data.apiKey);
  }
  // Push imported data to cloud
  if (isCloudEnabled) {
    for (const client of data.clients) {
      cloudSyncClient(client);
      for (const gen of (client.history || [])) cloudSyncGeneration(client.id, gen);
      for (const prog of (client.progression || [])) cloudSyncProgression(client.id, prog);
      for (const session of (client.massageSessions || [])) cloudSyncMassageSession(client.id, session);
    }
    if (data.nutritionConsultations) {
      for (const n of data.nutritionConsultations) cloudSyncNutritionConsultation(n);
    }
  }
  return data.clients.length;
}


// ─── Notifications (localStorage + optional Supabase) ───

function readNotifications() {
  try { return JSON.parse(localStorage.getItem(NOTIFICATIONS_KEY) || '[]'); }
  catch { return []; }
}

function writeNotifications(list) {
  localStorage.setItem(NOTIFICATIONS_KEY, JSON.stringify(list));
}

export function getNotifications() {
  return readNotifications().sort((a, b) => new Date(b.date) - new Date(a.date));
}

export function getUnreadNotificationCount() {
  return readNotifications().filter(n => !n.read).length;
}

export async function addNotification({ type, clientId, clientName, message }) {
  const list = readNotifications();
  const notif = {
    id: crypto.randomUUID(),
    type: type || 'info',
    clientId: clientId || null,
    clientName: clientName || '',
    message: message || '',
    date: new Date().toISOString(),
    read: false,
  };
  list.push(notif);
  writeNotifications(list);
  // Sync to Supabase if available
  if (isCloudEnabled) {
    const ownerId = await getCurrentOwnerId();
    supabase.from('notifications').upsert({ ...notif, owner_id: ownerId }).then(({ error }) => {
      if (error) console.warn('Notification sync failed:', error.message);
    });
  }
  return notif;
}

export function markNotificationRead(id) {
  const list = readNotifications();
  const notif = list.find(n => n.id === id);
  if (notif) {
    notif.read = true;
    writeNotifications(list);
    if (isCloudEnabled) {
      supabase.from('notifications').update({ read: true }).eq('id', id).then(() => {});
    }
  }
}

export function markAllNotificationsRead() {
  const list = readNotifications().map(n => ({ ...n, read: true }));
  writeNotifications(list);
}

export function syncReminderNotifications(clients) {
  const list = readNotifications();
  // Preserve read state of existing reminders before regenerating
  const readState = {};
  for (const n of list) {
    if (n.type === 'consultation_reminder') readState[n.id] = n.read;
  }
  const cleaned = list.filter(n => n.type !== 'consultation_reminder');

  for (const c of clients) {
    const consultations = readNutritionConsultations().filter(n => n.clientId === c.id).sort((a, b) => new Date(b.date) - new Date(a.date));
    if (consultations.length === 0) continue;
    const lastDate = consultations[0].date;
    const freqMonths = getClientReminderFrequency(c.id);
    const dueDate = new Date(lastDate);
    dueDate.setMonth(dueDate.getMonth() + freqMonths);
    const daysUntilDue = Math.ceil((dueDate - new Date()) / (1000 * 60 * 60 * 24));

    if (daysUntilDue <= 0) {
      const prenom = c.prenom || c.form?.prenom || 'Client';
      const severity = daysUntilDue <= -30 ? 'urgent' : 'overdue';
      const label = severity === 'urgent' ? 'Suivi urgent' : 'Suivi en retard';
      const reminderId = `reminder-${c.id}`;
      cleaned.push({
        id: reminderId,
        type: 'consultation_reminder',
        clientId: c.id,
        clientName: prenom,
        message: `${label} : ${prenom} (${Math.abs(daysUntilDue)}j de retard)`,
        date: dueDate.toISOString(),
        read: readState[reminderId] || false,
      });
    }
  }

  writeNotifications(cleaned);
}

// ─── Pack step completion sync from cycle_reviews ───
export async function syncCompletedStepsFromReviews() {
  if (!isCloudEnabled) return;

  const { data: submittedReviews, error } = await supabase
    .from('cycle_reviews')
    .select('id, client_id, step_number, submitted_at')
    .eq('status', 'submitted')
    .not('step_number', 'is', null);

  for (const review of error ? [] : (submittedReviews || [])) {
    if (!review.client_id || !review.step_number) continue;

    const client = readAll().find(c => c.id === review.client_id);
    if (!isContinuousFollowupPack(client?.packType)) continue;

    const schedule = client.packSchedule || [];
    const existing = schedule.find(s => s.stepNumber === review.step_number);

    // Ne rien faire si step inconnu — pas de push incomplet
    if (!matchesScheduledReview(review, existing)) continue;

    // Ne rien faire si déjà done
    if (existing.status === 'done') {
      await cloudSyncClient(client);
      continue;
    }

    const updatedSchedule = schedule.map(s =>
      s.stepNumber === review.step_number
        ? {
            ...s,
            status: 'done',
            completedAt: review.submitted_at,
          }
        : s
    );

    const saved = saveClient({ ...client, packSchedule: updatedSchedule });
    await cloudSyncClient(saved);
  }

  // L'appel est idempotent et rejoué à chaque synchronisation. Une panne
  // réseau au moment exact du bilan final ne peut donc pas faire disparaître
  // définitivement la proposition de continuité.
  for (const client of getClients()) {
    try {
      await publishRenewalOfferIfEligible(client);
    } catch (error) {
      console.warn('[renewal-offer] publication différée :', error?.message || error);
    }
  }
}

// ─── Pack follow-up notifications (stub, wire up later) ───
export function syncPackNotifications(clients) {
  // Placeholder — pack-based notifications will use buildPackFollowupSchedule
  // from services/packSystem.js. Currently no-op.
  return { clients };
}

// ─── Benoit dashboard notifications (packs, inactivité) ───
// Architecture alignée sur syncReminderNotifications :
//   - 100% localStorage (aucun appel Supabase ici)
//   - utilise les helpers existants (packSystem + data client en mémoire)
//   - ID stable par (type, clientId) → idempotent, pas de doublons
//   - préserve le read state avant régénération
//
// Règles métier V1 (seulement ce qui est calculable avec le modèle actuel) :
//   - benoit_pack_almost_done : pack de suivi actif, complétion >= 75 % mais < 100 %
//   - benoit_pack_completed   : pack de suivi avec toutes les étapes à l'état done
//   - benoit_client_inactive  : pack actif, aucune activité (progression, consultation
//                               nutrition, séance massage, step done) depuis 14 jours
//
// Règles écartées pour V1 (pas calculables sans entités dédiées) :
//   - "programme PT manquant" → aucune notion de program_tracker / training program
//     dans le modèle actuel (pas de table, pas de champ client).
//   - "sessionsRemaining / sessionsUsed" → les packs suivi sont step-based (packSchedule),
//     pas session-count-based. La progression est donc exprimée en % de steps done.
const BENOIT_NOTIF_TYPES = [
  'benoit_pack_almost_done',
  'benoit_pack_completed',
  'benoit_client_inactive',
];
const BENOIT_INACTIVE_DAYS = 14;
const BENOIT_ALMOST_DONE_PERCENT = 75;

function resolveLastActivityDate(client) {
  const candidates = [];
  if (Array.isArray(client.progression)) {
    for (const p of client.progression) {
      if (p?.date) candidates.push(new Date(p.date));
    }
  }
  if (Array.isArray(client.massageSessions)) {
    for (const s of client.massageSessions) {
      if (s?.date) candidates.push(new Date(s.date));
    }
  }
  const consults = readNutritionConsultations().filter(n => n.clientId === client.id);
  for (const c of consults) {
    if (c?.date) candidates.push(new Date(c.date));
  }
  if (Array.isArray(client.packSchedule)) {
    for (const step of client.packSchedule) {
      if (step?.status === 'done' && step?.completedAt) {
        candidates.push(new Date(step.completedAt));
      }
    }
  }
  // Historique séances Benoit V3 : priorité benoitLastSessionAt, fallback dernière de benoitSessionDates
  if (client.form?.benoitLastSessionAt) {
    candidates.push(new Date(client.form.benoitLastSessionAt));
  } else if (Array.isArray(client.form?.benoitSessionDates) && client.form.benoitSessionDates.length > 0) {
    const lastDate = client.form.benoitSessionDates[client.form.benoitSessionDates.length - 1];
    if (lastDate) candidates.push(new Date(lastDate));
  }
  const valid = candidates.filter(d => !isNaN(d.getTime()));
  if (valid.length === 0) return null;
  return new Date(Math.max(...valid.map(d => d.getTime())));
}

export function syncBenoitNotifications(clients) {
  if (!Array.isArray(clients)) return;
  const list = readNotifications();

  // Préserver le read state existant avant régénération
  const readState = {};
  for (const n of list) {
    if (BENOIT_NOTIF_TYPES.includes(n.type)) readState[n.id] = n.read;
  }

  // Nettoyer les notifications Benoit avant de recalculer
  const cleaned = list.filter(n => !BENOIT_NOTIF_TYPES.includes(n.type));
  const now = new Date();
  const inactiveCutoffMs = BENOIT_INACTIVE_DAYS * 24 * 60 * 60 * 1000;

  for (const c of clients) {
    if (!c || !c.id) continue;
    // Ne ciblait que les clients Benoit. Toutes les fiches étant désormais à
    // Anissa, cette boucle ne produit plus aucune notification — elle part avec
    // le reste du suivi BenFitCoach une fois le périmètre validé.
    if ((c.createdBy || PRATICIENNE_PAR_DEFAUT) === 'anissa') continue;

    const prenom = c.prenom || c.form?.prenom || 'Client';
    const packType = c.packType || null;
    const isSuiviPack = isContinuousFollowupPack(packType);

    // ─── Priorité V2 : si le compteur séances client.form.benoitSessionsTotal
    // est défini (> 0), on utilise cette logique en premier. Sinon fallback
    // sur la logique packSchedule / getPackCompletion() pré-existante.
    const rawSessionsTotal = Number(c.form?.benoitSessionsTotal);
    const rawSessionsDone = Number(c.form?.benoitSessionsDone);
    const hasSessionCounter = Number.isFinite(rawSessionsTotal) && rawSessionsTotal > 0;

    if (hasSessionCounter) {
      const sTotal = rawSessionsTotal;
      const sDone = Number.isFinite(rawSessionsDone) && rawSessionsDone > 0 ? rawSessionsDone : 0;
      const sRemaining = sTotal - sDone;

      // ── 1. pack_completed (via séances) ── prioritaire sur almost_done
      if (sDone >= sTotal) {
        const id = `benoit_pack_completed-${c.id}`;
        cleaned.push({
          id,
          type: 'benoit_pack_completed',
          clientId: c.id,
          clientName: prenom,
          message: `Pack terminé : ${prenom} (${sDone}/${sTotal} séances)`,
          date: new Date().toISOString(),
          read: readState[id] || false,
        });
      }
      // ── 2. pack_almost_done (via séances) ── remaining <= 2
      else if (sRemaining <= 2 && sDone < sTotal) {
        const id = `benoit_pack_almost_done-${c.id}`;
        cleaned.push({
          id,
          type: 'benoit_pack_almost_done',
          clientId: c.id,
          clientName: prenom,
          message: `Pack presque terminé : ${prenom} (${sRemaining} séance${sRemaining > 1 ? 's' : ''} restante${sRemaining > 1 ? 's' : ''})`,
          date: new Date().toISOString(),
          read: readState[id] || false,
        });
      }
    } else {
      // ─── Fallback : logique packSchedule / getPackCompletion() pré-existante
      let completion = null;
      try {
        completion = isSuiviPack ? getPackCompletion(c) : null;
      } catch {
        completion = null;
      }

      // ── 1. pack_completed ── (prioritaire sur almost_done)
      if (completion && completion.total > 0 && completion.done >= completion.total) {
        const id = `benoit_pack_completed-${c.id}`;
        cleaned.push({
          id,
          type: 'benoit_pack_completed',
          clientId: c.id,
          clientName: prenom,
          message: `Pack terminé : ${prenom}`,
          date: new Date().toISOString(),
          read: readState[id] || false,
        });
      }
      // ── 2. pack_almost_done ──
      else if (completion && completion.total > 0 && completion.percent >= BENOIT_ALMOST_DONE_PERCENT) {
        const remaining = completion.total - completion.done;
        const id = `benoit_pack_almost_done-${c.id}`;
        cleaned.push({
          id,
          type: 'benoit_pack_almost_done',
          clientId: c.id,
          clientName: prenom,
          message: `Pack presque terminé : ${prenom} (${remaining} étape${remaining > 1 ? 's' : ''} restante${remaining > 1 ? 's' : ''})`,
          date: new Date().toISOString(),
          read: readState[id] || false,
        });
      }
    }

    // ── 3. client_inactive ──
    // Exige un pack actif (sinon pas d'attente de suivi)
    if (packType) {
      const lastActivity = resolveLastActivityDate(c);
      const reference = lastActivity || (c.packStartedAt ? new Date(c.packStartedAt) : null);
      if (reference && !isNaN(reference.getTime())) {
        const delta = now.getTime() - reference.getTime();
        if (delta >= inactiveCutoffMs) {
          const days = Math.floor(delta / (24 * 60 * 60 * 1000));
          const id = `benoit_client_inactive-${c.id}`;
          cleaned.push({
            id,
            type: 'benoit_client_inactive',
            clientId: c.id,
            clientName: prenom,
            message: `Client inactif : ${prenom} (${days}j sans activité)`,
            date: reference.toISOString(),
            read: readState[id] || false,
          });
        }
      }
    }
  }

  writeNotifications(cleaned);
}

// ─── Draft persistence for nutrition consultations ───

const DRAFT_KEY_PREFIX = 'bfc_draft_';
const DRAFT_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 jours

export function getDraftKey(clientId, consultationId) {
  return `${DRAFT_KEY_PREFIX}${clientId}_${consultationId || 'new'}`;
}

export function saveDraft(clientId, consultationId, { plan, supplements, recipes }) {
  if (!clientId) return;
  const key = getDraftKey(clientId, consultationId);
  const entry = {
    plan: plan || '',
    supplements: supplements || '',
    recipes: recipes || '',
    savedAt: Date.now(),
  };
  try {
    localStorage.setItem(key, JSON.stringify(entry));
  } catch (e) {
    console.warn('[DRAFT] localStorage full:', e.message);
  }
}

export function loadDraft(clientId, consultationId) {
  if (!clientId) return null;
  try {
    const raw = localStorage.getItem(getDraftKey(clientId, consultationId));
    if (!raw) return null;
    const entry = JSON.parse(raw);
    // TTL : ignorer les drafts de plus de 7 jours
    if (!entry.savedAt || Date.now() - entry.savedAt > DRAFT_TTL_MS) {
      clearDraft(clientId, consultationId);
      return null;
    }
    return entry;
  } catch {
    return null;
  }
}

export function clearDraft(clientId, consultationId) {
  if (!clientId) return;
  localStorage.removeItem(getDraftKey(clientId, consultationId));
}

// Nettoyage global des drafts expirés (appeler au boot)
export function purgeExpiredDrafts() {
  const keys = Object.keys(localStorage).filter(k => k.startsWith(DRAFT_KEY_PREFIX));
  for (const key of keys) {
    try {
      const entry = JSON.parse(localStorage.getItem(key) || '{}');
      if (!entry.savedAt || Date.now() - entry.savedAt > DRAFT_TTL_MS) {
        localStorage.removeItem(key);
      }
    } catch {
      localStorage.removeItem(key);
    }
  }
}

// ─── Cycle Reviews ───

export async function createCycleReview(clientId, consultationId, ownerId) {
  if (!isCloudEnabled) return null;
  const token = crypto.randomUUID();
  const { data, error } = await supabase
    .from('cycle_reviews')
    .insert({
      token,
      client_id: clientId,
      consultation_id: consultationId || null,
      owner_id: ownerId,
      status: 'sent',
    })
    .select('token')
    .single();
  if (error) {
    console.warn('createCycleReview failed:', error.message);
    return null;
  }
  return data.token;
}

export async function getCycleReviews(clientId) {
  if (!isCloudEnabled) return [];
  const { data, error } = await supabase
    .from('cycle_reviews')
    .select('*')
    .eq('client_id', clientId)
    .order('created_at', { ascending: false });
  if (error) return [];
  return data || [];
}
